import * as React from "react";
import {
	Upload,
	FileText,
	Clock,
	Zap,
	Timer,
	Loader2,
	AlertCircle,
	Crown,
	Languages,
	MoreHorizontal,
	ChevronUp,
	ArrowRight,
	RefreshCw,
} from "lucide-react";
import { useTranslation } from "react-i18next";

import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { isAxiosError } from "axios";
import {
	CoinService,
	type CostEstimate,
	type TrialRollingSnapshot,
	PodcastLength,
} from "@/lib/coin/coin.service";
import { PodcastService } from "@/lib/podcast/podcast.service";
import {
	BaseMediumService,
	type TextExtractionResult,
} from "@/lib/baseMedium/baseMedium.service";
import { useCoinBalance, useCoinStore } from "@/lib/coin/coin.store";
import { toast } from "sonner";
import { useEffect, useCallback } from "react";
import { PricingModal } from "@/features/pricing/components/pricingModal.component";
import AuthService from "@/lib/auth/AuthApi.service";
import { AnalyticsService } from "@/lib/analytics/analytics.service";
import { useSubscription } from "@/lib/subscription/useSubscription.hook";
import { useUpgradeNudge } from "@/lib/nudges/useUpgradeNudge.hook";

type KnowledgeLevel = "beginner" | "intermediate" | "expert";
type PodcastLanguage = "de" | "en" | "it" | "ru" | "es" | "fr";

// Maximum file size in bytes (50MB)
const MAX_FILE_SIZE = 50 * 1024 * 1024;

interface UploadedPaper {
	title: string;
	file: File;
}

interface CreatePodWorkspaceModalProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onCreate: (payload: {
		blobLink: string;
		title: string;
		knowledgeLevel: KnowledgeLevel;
		podcastLength: PodcastLength;
		language: PodcastLanguage;
	}) => Promise<{ id: string }>;
	defaultKnowledgeLevel?: KnowledgeLevel;
	defaultPodcastLength?: PodcastLength;
	/** Optional language override. If not provided, uses current i18n language. */
	defaultLanguage?: PodcastLanguage;
	workspaceId?: string; // Optional workspace ID to fetch podcast info
	/** Optional callback fired after a podcast has been successfully created and approved. */
	onPodcastCreated?: () => void;
	/** Optional callback fired when the modal closes, allowing parent to refetch data. */
	onClose?: () => void;
	/** Optional initial file to pre-populate the upload step (from drag & drop) */
	initialFile?: File;
	/** Optional storage file ID to auto-load and extract (from funnel flow) */
	initialStorageFileId?: string;
}

type UploadValue = {
	title: string;
	file: File | null;
};

// Available podcast languages with their metadata
const PODCAST_LANGUAGES: Array<{
	value: PodcastLanguage;
	label: string;
	flag: string;
}> = [
	{ value: "en", label: "EN", flag: "🇬🇧" },
	{ value: "de", label: "DE", flag: "🇩🇪" },
	{ value: "it", label: "IT", flag: "🇮🇹" },
	{ value: "ru", label: "RU", flag: "🇷🇺" },
	{ value: "es", label: "ES", flag: "🇪🇸" },
	{ value: "fr", label: "FR", flag: "🇫🇷" },
];

// Base metadata for podcast lengths (multipliers will come from backend)
export const podcastLengthsBase: Array<{
	value: PodcastLength;
	labelKey: string;
	descriptionKey: string;
	durationKey: string;
	icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
	colorClass: string;
	recommended?: boolean;
}> = [
	{
		value: PodcastLength.SHORT,
		labelKey: "workspace.create.lengthOptions.quickOverview.label",
		icon: Zap,
		descriptionKey: "workspace.create.lengthOptions.quickOverview.description",
		durationKey: "workspace.create.lengthOptions.quickOverview.duration",
		colorClass: "text-yellow-500",
	},
	{
		value: PodcastLength.MEDIUM,
		labelKey: "workspace.create.lengthOptions.standardDeepDive.label",
		icon: Clock,
		descriptionKey:
			"workspace.create.lengthOptions.standardDeepDive.description",
		durationKey: "workspace.create.lengthOptions.standardDeepDive.duration",
		colorClass: "text-blue-500",
		recommended: true,
	},
	{
		value: PodcastLength.LONG,
		labelKey: "workspace.create.lengthOptions.comprehensiveAnalysis.label",
		icon: Timer,
		descriptionKey:
			"workspace.create.lengthOptions.comprehensiveAnalysis.description",
		durationKey:
			"workspace.create.lengthOptions.comprehensiveAnalysis.duration",
		colorClass: "text-purple-500",
	},
];

export function CreatePodWorkspaceModal({
	open,
	onOpenChange,
	onCreate,
	defaultKnowledgeLevel = "intermediate",
	defaultPodcastLength = PodcastLength.MEDIUM,
	defaultLanguage = undefined,
	onPodcastCreated,
	onClose,
	initialFile,
	initialStorageFileId,
}: CreatePodWorkspaceModalProps) {
	const { t, i18n } = useTranslation();

	// Get the current i18n language and normalize it to 'en' or 'de'
	const currentI18nLang = i18n.language.startsWith("de") ? "de" : "en";
	const getCurrentLanguage = (): PodcastLanguage => {
		return defaultLanguage || currentI18nLang;
	};

	const [step, setStep] = React.useState<1 | 2>(1);
	const [uploadValue, setUploadValue] = React.useState<UploadValue>({
		title: initialFile
			? initialFile.name.replace(/\.(pdf|docx|txt|pptx)$/i, "")
			: "",
		file: initialFile || null,
	});
	const [knowledgeLevel, setKnowledgeLevel] = React.useState<KnowledgeLevel>(
		defaultKnowledgeLevel,
	);
	const [podcastLength, setPodcastLength] =
		React.useState<PodcastLength>(defaultPodcastLength);
	const [language, setLanguage] = React.useState<PodcastLanguage>(
		getCurrentLanguage(),
	);
	const [extractionResult, setExtractionResult] =
		React.useState<TextExtractionResult | null>(null);
	const [costEstimates, setCostEstimates] = React.useState<
		Record<PodcastLength, CostEstimate | null>
	>({
		[PodcastLength.SHORT]: null,
		[PodcastLength.MEDIUM]: null,
		[PodcastLength.LONG]: null,
	});
	const [trialRolling, setTrialRolling] =
		React.useState<TrialRollingSnapshot | null>(null);
	const { balance: userBalance } = useCoinBalance();
	const { subscriptionStatus } = useSubscription();
	const {
		eligible: trialEligible,
		canShow,
		markShown,
		markClicked,
		incrementCreateFlow,
	} = useUpgradeNudge();
	const [showRepeatIntent, setShowRepeatIntent] = React.useState(false);
	const createFlowCountedRef = React.useRef(false);
	const [isUploading, setIsUploading] = React.useState(false);
	const [uploadProgress, setUploadProgress] = React.useState(0);
	const [isExtracting, setIsExtracting] = React.useState(false);
	const [extractionProgress, setExtractionProgress] = React.useState(0);
	const [isApproving, setIsApproving] = React.useState(false);
	const [hasAttemptedUpload, setHasAttemptedUpload] = React.useState(false);
	const [showPricingModal, setShowPricingModal] = React.useState(false);
	const [estimateFailed, setEstimateFailed] = React.useState(false);
	const [isEstimating, setIsEstimating] = React.useState(false);
	const refreshBalance = useCoinStore((state) => state.refreshBalance);

	const canContinueFromUpload = Boolean(
		uploadValue.title && uploadValue.file && !isUploading && !isExtracting,
	);

	// Count create-flow visits for the repeat-intent nudge (N3).
	React.useEffect(() => {
		if (step !== 2 || !open || createFlowCountedRef.current) return;
		createFlowCountedRef.current = true;
		const count = incrementCreateFlow();
		if (trialEligible && count >= 2 && canShow("repeat_intent")) {
			markShown("repeat_intent", {
				coin_balance: userBalance,
				trigger: "create_flow_repeat",
			});
			setShowRepeatIntent(true);
		}
		// Only once per modal open when entering step 2.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [step, open]);

	// Fetch cost estimates for all podcast lengths based on extracted chapter data
	const fetchCostEstimates = React.useCallback(
		async (result: TextExtractionResult) => {
			setEstimateFailed(false);
			setIsEstimating(true);
			try {
				// Calculate total characters from all chapters
				const totalChars = result.chapters.reduce(
					(sum, ch) => sum + ch.textLength,
					0,
				);
				const totalPages = result.chapters.reduce(
					(sum, ch) => sum + ch.pageCount,
					0,
				);
				const chapterCount = result.totalChapters;

				const [shortEstimate, mediumEstimate, longEstimate] = await Promise.all(
					[
						CoinService.estimatePodcastCostFromData({
							pageCount: totalPages,
							estimatedTextChars: totalChars,
							chapterCount,
							podcastLength: PodcastLength.SHORT,
						}),
						CoinService.estimatePodcastCostFromData({
							pageCount: totalPages,
							estimatedTextChars: totalChars,
							chapterCount,
							podcastLength: PodcastLength.MEDIUM,
						}),
						CoinService.estimatePodcastCostFromData({
							pageCount: totalPages,
							estimatedTextChars: totalChars,
							chapterCount,
							podcastLength: PodcastLength.LONG,
						}),
					],
				);

				setCostEstimates({
					[PodcastLength.SHORT]: shortEstimate.estimate,
					[PodcastLength.MEDIUM]: mediumEstimate.estimate,
					[PodcastLength.LONG]: longEstimate.estimate,
				});
				setTrialRolling(shortEstimate.trialRolling ?? null);
			} catch (error) {
				console.error("Failed to fetch cost estimates:", error);
				// Without an estimate the Generate button stays disabled, so this
				// has to surface to the user instead of failing silently.
				setCostEstimates({
					[PodcastLength.SHORT]: null,
					[PodcastLength.MEDIUM]: null,
					[PodcastLength.LONG]: null,
				});
				setTrialRolling(null);
				setEstimateFailed(true);
				AnalyticsService.track("cost_estimate_failed", {
					status: isAxiosError(error) ? error.response?.status : undefined,
					chapter_count: result.totalChapters,
				});
			} finally {
				setIsEstimating(false);
			}
		},
		[],
	);

	// Fetch cost estimates when extraction result is available
	useEffect(() => {
		if (extractionResult && step === 2) {
			fetchCostEstimates(extractionResult);
		}
	}, [extractionResult, step, fetchCostEstimates]);

	// Update uploadValue when initialFile changes (on modal open with drag-dropped file)
	useEffect(() => {
		if (open && initialFile) {
			setUploadValue({
				title: initialFile.name.replace(/\.(pdf|docx|txt|pptx)$/i, ""),
				file: initialFile,
			});
		}
	}, [open, initialFile]);

	// Handle initialStorageFileId (from funnel flow)
	useEffect(() => {
		if (!open || !initialStorageFileId || extractionResult) return;

		const loadStorageFile = async () => {
			try {
				setIsExtracting(true);
				setExtractionProgress(30);

				// Get Firebase token
				const token =
					await AuthService.getInstance().auth.currentUser?.getIdToken();

				if (!token) {
					throw new Error("Not authenticated");
				}

				// Call anonymous/extract-chapters endpoint
				const baseUrl = import.meta.env.VITE_API_URL || "http://localhost:3000";
				const response = await fetch(
					`${baseUrl}/api/v1/anonymous/extract-chapters`,
					{
						method: "POST",
						headers: {
							"Content-Type": "application/json",
							Authorization: `Bearer ${token}`,
						},
						body: JSON.stringify({ storageFileId: initialStorageFileId }),
					},
				);

				if (!response.ok) {
					throw new Error("Failed to load storage file");
				}

				setExtractionProgress(70);

				const data = await response.json();

				// Set the extraction result
				setExtractionResult(data as TextExtractionResult);

				// Set the upload value with the title
				setUploadValue({
					title: data.title || "Untitled",
					file: null, // No file object needed since it's already uploaded
				});

				setExtractionProgress(100);

				// Automatically advance to step 2
				setStep(2);
			} catch (error) {
				console.error("Failed to load storage file:", error);
				toast.error(t("workspace.create.failedToLoadFile"));
			} finally {
				setIsExtracting(false);
				setExtractionProgress(0);
			}
		};

		loadStorageFile();
	}, [open, initialStorageFileId, extractionResult, t]);

	const handleClose = (nextOpen: boolean) => {
		onOpenChange(nextOpen);
		if (!nextOpen) {
			// reset when closing
			setStep(1);
			setUploadValue({ title: "", file: null });
			setKnowledgeLevel(defaultKnowledgeLevel);
			setPodcastLength(defaultPodcastLength);
			setLanguage(getCurrentLanguage());
			setExtractionResult(null);
			setCostEstimates({
				[PodcastLength.SHORT]: null,
				[PodcastLength.MEDIUM]: null,
				[PodcastLength.LONG]: null,
			});
			setTrialRolling(null);
			setIsUploading(false);
			setUploadProgress(0);
			setIsExtracting(false);
			setExtractionProgress(0);
			setIsApproving(false);
			setHasAttemptedUpload(false);
			setEstimateFailed(false);
			setShowRepeatIntent(false);
			createFlowCountedRef.current = false;

			// Trigger refetch in parent component if callback is provided
			onClose?.();
		}
	};

	const handleContinueToStep2 = useCallback(async () => {
		if (!uploadValue.title || !uploadValue.file) return;

		// Mark that we've attempted upload to prevent infinite loops
		setHasAttemptedUpload(true);

		// Phase 1: Real upload progress
		setIsUploading(true);
		setUploadProgress(0);

		// Phase 2: Extract text from PDF (includes upload + processing)
		setIsExtracting(true);
		setExtractionProgress(0);

		// Set up extraction progress simulator (starts after upload completes)
		let extractionInterval: NodeJS.Timeout | null = null;

		try {
			const result = await BaseMediumService.extractOnly(
				uploadValue.file,
				uploadValue.title,
				// Real upload progress callback
				(progress) => {
					setUploadProgress(progress);
					// When upload reaches 100%, transition to extraction phase
					if (progress === 100) {
						setIsUploading(false);
						// Start simulating extraction progress (server-side processing)
						extractionInterval = setInterval(() => {
							setExtractionProgress((prev) => {
								if (prev >= 85) return prev; // Cap at 85% until actual completion
								// Slower progress as we get higher for more realistic feel
								if (prev < 40) return prev + 3;
								if (prev < 70) return prev + 2;
								return prev + 1;
							});
						}, 270);
					}
				},
			);
			console.log("Text extraction completed:", result);

			// Track successful document upload
			AnalyticsService.track("document_uploaded", {
				file_type: uploadValue.file?.type,
				file_size_mb: uploadValue.file
					? Math.round((uploadValue.file.size / 1024 / 1024) * 100) / 100
					: undefined,
				title: uploadValue.title,
				chapter_count: result.totalChapters,
			});

			// Clear interval and complete extraction progress
			if (extractionInterval) clearInterval(extractionInterval);
			setExtractionProgress(90);
			setExtractionResult(result);

			// Fetch cost estimates immediately after extraction
			await fetchCostEstimates(result);
			setExtractionProgress(100);

			// Small pause to show 100% completion
			await new Promise((resolve) => setTimeout(resolve, 300));

			// Go directly to next step
			setStep(2);
			setExtractionProgress(0);
		} catch (error) {
			console.error("Failed to extract text from PDF:", error);
			if (extractionInterval) clearInterval(extractionInterval);

			// Check if it's a timeout error
			const errorMessage =
				error instanceof Error ? error.message : String(error);
			const isTimeoutError =
				errorMessage.includes("timeout") ||
				errorMessage.includes("ECONNABORTED");
			const isNetworkError =
				errorMessage.includes("network") ||
				errorMessage.includes("EAI_AGAIN") ||
				errorMessage.includes("ECONNREFUSED") ||
				errorMessage.includes("ETIMEDOUT") ||
				errorMessage.includes("getaddrinfo") ||
				errorMessage.includes("fetch failed");

			if (isTimeoutError) {
				toast.error(t("workspace.create.uploadTimeout"), {
					description: t("workspace.create.uploadTimeoutDescription"),
				});
			} else if (isNetworkError) {
				toast.error(t("workspace.create.networkError"));
			} else {
				toast.error(
					t("workspace.create.extractionFailed", { message: errorMessage }),
				);
			}

			// Reset states on error
			setIsUploading(false);
			setIsExtracting(false);
			setExtractionProgress(0);
			setUploadProgress(0);
		} finally {
			if (extractionInterval) clearInterval(extractionInterval);
			setIsUploading(false);
			setIsExtracting(false);
		}
	}, [uploadValue, fetchCostEstimates, t]);

	// Reset hasAttemptedUpload when file changes to allow retry
	useEffect(() => {
		if (uploadValue.file) {
			setHasAttemptedUpload(false);
		}
	}, [uploadValue.file]);

	// Automatically start upload when file and title are provided
	useEffect(() => {
		if (
			step === 1 &&
			uploadValue.title &&
			uploadValue.file &&
			!isUploading &&
			!isExtracting &&
			!extractionResult &&
			!hasAttemptedUpload // Prevent infinite loop - only attempt once
		) {
			// Small delay to allow UI to update
			const timer = setTimeout(() => {
				handleContinueToStep2();
			}, 500);
			return () => clearTimeout(timer);
		}
	}, [
		uploadValue.title,
		uploadValue.file,
		step,
		isUploading,
		isExtracting,
		extractionResult,
		hasAttemptedUpload,
		handleContinueToStep2,
	]);

	const handleConfirm = async () => {
		if (!extractionResult || !costEstimates[podcastLength]) {
			console.error("No extraction result or cost estimate available");
			return;
		}

		// Create workspace, baseMedium, wait for podcast creation, then approve
		setIsApproving(true);
		try {
			// Step 1: Create workspace and baseMedium (triggers transcription)
			const workspace = await onCreate({
				blobLink: extractionResult.blobLink,
				title: uploadValue.title,
				knowledgeLevel,
				podcastLength,
				language,
			});

			// Step 2: Poll for podcast to be created (transcription creates it)
			console.log(
				"Waiting for podcast to be created in workspace:",
				workspace.id,
			);
			let attempts = 0;
			const maxAttempts = 30; // 30 seconds max
			let podcastId: string | null = null;

			while (attempts < maxAttempts && !podcastId) {
				await new Promise((resolve) => setTimeout(resolve, 1000)); // Wait 1 second

				try {
					const podcasts = await PodcastService.getPodcastsByWorkspace(
						workspace.id,
					);
					if (
						podcasts.length > 0 &&
						podcasts[0].chapters &&
						podcasts[0].chapters.length > 0
					) {
						podcastId = podcasts[0].id;
						console.log("Podcast created with ID:", podcastId);
						break;
					}
				} catch (error) {
					console.log(
						`Polling attempt ${attempts + 1}/${maxAttempts} failed:`,
						error,
					);
				}

				attempts++;
			}

			if (!podcastId) {
				throw new Error(t("workspace.create.podcastTimeout"));
			}

			// Step 3: Approve the cost and start generation
			console.log("Approving cost for podcast:", podcastId);
			await PodcastService.approveCost(podcastId);

			// Track podcast creation conversion
			AnalyticsService.track("podcast_created", {
				podcast_id: podcastId,
				workspace_id: workspace.id,
				podcast_length: podcastLength,
				language,
				title: uploadValue.title,
			});

			// Step 4: Notify listeners that the podcast has been created so they can refetch workspaces
			try {
				onPodcastCreated?.();
			} catch (callbackError) {
				console.error(
					"onPodcastCreated callback threw an error:",
					callbackError,
				);
			}

			// Step 5: Refresh the global coin balance so sidebar updates immediately
			console.log("Refreshing coin balance...");
			await refreshBalance();

			console.log("Podcast generation started successfully!");
			handleClose(false);
		} catch (error) {
			console.error("Failed to create workspace and start generation:", error);
			if (!(isAxiosError(error) && error.response?.status === 429)) {
				toast.error(t("workspace.create.createFailed"));
			}
		} finally {
			setIsApproving(false);
		}
	};

	const headerTitle =
		step === 1
			? t("workspace.create.uploadTitle")
			: t("workspace.create.customizeTitle");

	return (
		<>
			<Dialog open={open} onOpenChange={handleClose}>
				<DialogContent className="sm:max-w-[600px] max-h-[85vh] flex flex-col">
					<DialogHeader>
						<DialogTitle>{headerTitle}</DialogTitle>
					</DialogHeader>

					<div className="flex flex-col gap-4 sm:gap-6 p-3 sm:p-4 overflow-y-auto flex-1">
						{step === 1 ? (
							<UploadPaperStep
								value={uploadValue}
								onValueChange={setUploadValue}
								isUploading={isUploading}
								uploadProgress={uploadProgress}
								isExtracting={isExtracting}
								extractionProgress={extractionProgress}
							/>
						) : (
							<ConfigurationStep
								paperTitle={uploadValue.title}
								onTitleChange={(newTitle) =>
									setUploadValue({ ...uploadValue, title: newTitle })
								}
								podcastLength={podcastLength}
								onPodcastLengthChange={setPodcastLength}
								language={language}
								onLanguageChange={setLanguage}
								chapterCount={extractionResult?.totalChapters || null}
								costEstimates={costEstimates}
								userBalance={userBalance}
								onShowPricingModal={() => {
									const estimate = costEstimates[podcastLength];
									if (
										trialEligible &&
										userBalance !== null &&
										estimate &&
										userBalance < estimate.totalCost
									) {
										markClicked("out_of_coins", {
											coin_balance: userBalance,
											required_coins: estimate.totalCost,
											missing_coins: estimate.totalCost - userBalance,
											trigger: "create_modal",
										});
									}
									setShowPricingModal(true);
								}}
								extractionResult={extractionResult}
								trialRolling={trialRolling}
								estimateFailed={estimateFailed}
								showRepeatIntent={showRepeatIntent}
								onRepeatIntentCta={() => {
									markClicked("repeat_intent", {
										coin_balance: userBalance,
										trigger: "create_modal",
									});
									setShowPricingModal(true);
								}}
							/>
						)}
					</div>

					<DialogFooter className="flex-col-reverse sm:flex-row sm:justify-between gap-3 sm:gap-0">
						<div className="flex w-full sm:w-auto">
							{step === 2 ? (
								<Button
									variant="ghost"
									size="sm"
									onClick={() => setStep(1)}
									className="w-full sm:w-auto"
								>
									{t("workspace.create.back")}
								</Button>
							) : (
								<Button
									variant="ghost"
									size="sm"
									onClick={() => handleClose(false)}
									className="w-full sm:w-auto"
								>
									{t("common.cancel")}
								</Button>
							)}
						</div>
						<div className="flex flex-col sm:flex-row items-center gap-2 w-full sm:w-auto">
							{step === 1 ? (
								<Button
									onClick={handleContinueToStep2}
									disabled={!canContinueFromUpload}
									size="default"
									className="w-full sm:w-auto min-w-[120px]"
								>
									{isUploading ? (
										<>
											<Loader2 className="mr-2 h-4 w-4 animate-spin" />
											{t("workspace.create.uploading")}
										</>
									) : isExtracting ? (
										<>
											<Loader2 className="mr-2 h-4 w-4 animate-spin" />
											{t("workspace.create.extractingText")}
										</>
									) : (
										t("workspace.create.continue")
									)}
								</Button>
							) : (
								<>
									{(() => {
										const estimate = costEstimates[podcastLength];

										// No estimate means Generate can never be enabled —
										// offer a way out instead of a dead button.
										if (estimateFailed) {
											return (
												<Button
													onClick={() => {
														if (extractionResult) {
															fetchCostEstimates(extractionResult);
														}
													}}
													disabled={isEstimating}
													size="default"
													className="w-full sm:w-auto min-w-[160px]"
												>
													{isEstimating ? (
														<Loader2 className="mr-2 h-4 w-4 animate-spin" />
													) : (
														<RefreshCw className="mr-2 h-4 w-4" />
													)}
													{t("workspace.create.retryEstimate")}
												</Button>
											);
										}

										const blockedByTrialCap =
											estimate !== null &&
											trialRolling !== null &&
											trialRolling.used + estimate.totalCost >
												trialRolling.limit;
										const cannotAfford =
											userBalance !== null &&
											estimate !== null &&
											userBalance < estimate.totalCost;

										const usePaidUpgradeCta =
											blockedByTrialCap ||
											subscriptionStatus === "TRIALING" ||
											subscriptionStatus === "ACTIVE";
										// Active subscribers can buy a top-up pack, so don't
										// push them at a plan change they may not need.
										const useTopUpCta =
											!blockedByTrialCap && subscriptionStatus === "ACTIVE";

										// When the trial sprint is blocking us, the body
										// banner already shows a primary upgrade CTA — render
										// only the disabled Generate here to avoid two
										// competing "upgrade" buttons.
										if (blockedByTrialCap) {
											return (
												<Button
													variant="ghost"
													disabled
													className="w-full sm:w-auto opacity-50"
												>
													{t("workspace.create.generatePodcast")}
												</Button>
											);
										}

										if (cannotAfford) {
											return (
												<>
													<Button
														variant="ghost"
														disabled
														className="w-full sm:w-auto opacity-50"
													>
														{t("workspace.create.generatePodcast")}
													</Button>
													<Button
														variant="default"
														onClick={() => setShowPricingModal(true)}
														className="w-full sm:w-auto bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white border-0"
													>
														<Crown className="mr-2 h-4 w-4" />
														{useTopUpCta
															? t("workspace.create.topUpCoins")
															: usePaidUpgradeCta
																? t("workspace.create.upgradePlan")
																: t("workspace.create.upgrade")}
													</Button>
												</>
											);
										}

										return (
											<Button
												onClick={handleConfirm}
												disabled={
													!extractionResult ||
													!costEstimates[podcastLength] ||
													isApproving ||
													extractionResult?.hasInsufficientText
												}
												size="default"
												className="w-full sm:w-auto min-w-[160px]"
											>
												{isApproving ? (
													<>
														<Loader2 className="mr-2 h-4 w-4 animate-spin" />
														{t("workspace.create.generating")}
													</>
												) : (
													t("workspace.create.generatePodcast")
												)}
											</Button>
										);
									})()}
								</>
							)}
						</div>
					</DialogFooter>
				</DialogContent>
			</Dialog>
			<PricingModal
				source="workspace"
				open={showPricingModal}
				onOpenChange={(isOpen) => {
					setShowPricingModal(isOpen);
					// When the pricing modal closes, the user may have upgraded or
					// activated paid billing. Refresh both the global coin balance
					// AND re-fetch the cost estimate (it carries the trial sprint
					// window state, which gets stale after `endTrialNow`).
					if (!isOpen) {
						refreshBalance();
						if (extractionResult && step === 2) {
							fetchCostEstimates(extractionResult);
						}
					}
				}}
			/>
		</>
	);
}

function UploadPaperStep({
	value,
	onValueChange,
	isUploading,
	uploadProgress,
	isExtracting,
	extractionProgress,
}: {
	value: UploadValue;
	onValueChange: (next: UploadValue) => void;
	isUploading: boolean;
	uploadProgress: number;
	isExtracting: boolean;
	extractionProgress: number;
}) {
	const { t } = useTranslation();
	const [dragActive, setDragActive] = React.useState(false);
	const inputRef = React.useRef<HTMLInputElement>(null);
	const fileInputId = React.useId();

	const handleDrag = (e: React.DragEvent) => {
		e.preventDefault();
		e.stopPropagation();
		if (e.type === "dragenter" || e.type === "dragover") {
			setDragActive(true);
		} else if (e.type === "dragleave") {
			setDragActive(false);
		}
	};

	const handleDrop = (e: React.DragEvent) => {
		e.preventDefault();
		e.stopPropagation();
		setDragActive(false);
		if (e.dataTransfer.files?.[0]) {
			const droppedFile = e.dataTransfer.files[0];
			const allowedTypes = [
				"application/pdf",
				"application/vnd.openxmlformats-officedocument.wordprocessingml.document",
				"text/plain",
				"application/vnd.openxmlformats-officedocument.presentationml.presentation",
			];
			const allowedExtensions = [".pdf", ".docx", ".txt", ".pptx"];
			const fileExtension = droppedFile.name
				.toLowerCase()
				.substring(droppedFile.name.lastIndexOf("."));

			if (
				allowedTypes.includes(droppedFile.type) ||
				allowedExtensions.includes(fileExtension)
			) {
				// Check file size
				if (droppedFile.size > MAX_FILE_SIZE) {
					toast.error(
						t("workspace.create.fileTooLarge", {
							max: MAX_FILE_SIZE / 1024 / 1024,
							size: (droppedFile.size / 1024 / 1024).toFixed(2),
						}),
					);
					return;
				}
				onValueChange({
					title:
						value.title ||
						droppedFile.name.replace(/\.(pdf|docx|txt|pptx)$/i, ""),
					file: droppedFile,
				});
			}
		}
	};

	const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (e.target.files?.[0]) {
			const selectedFile = e.target.files[0];
			// Check file size
			if (selectedFile.size > MAX_FILE_SIZE) {
				toast.error(
					t("workspace.create.fileTooLarge", {
						max: MAX_FILE_SIZE / 1024 / 1024,
						size: (selectedFile.size / 1024 / 1024).toFixed(2),
					}),
				);
				// Reset the file input
				if (e.target) {
					e.target.value = "";
				}
				return;
			}
			onValueChange({
				title:
					value.title ||
					selectedFile.name.replace(/\.(pdf|docx|txt|pptx)$/i, ""),
				file: selectedFile,
			});
		}
	};

	const handleRemoveFile = (e: React.MouseEvent) => {
		e.stopPropagation();
		onValueChange({ ...value, file: null });
	};

	const handleFileUploadKeyDown = (e: React.KeyboardEvent) => {
		// Handle keyboard interaction for accessibility
		if (e.key === "Enter" || e.key === " ") {
			e.preventDefault();
			if (!isUploading && !isExtracting && !value.file) {
				inputRef.current?.click();
			}
		}
	};

	return (
		<div className="grid gap-6">
			<div className="grid gap-2">
				<Label htmlFor={fileInputId}>{t("workspace.create.paperPdf")}</Label>
				{/* biome-ignore lint/a11y/useSemanticElements: Need div instead of button to avoid nested buttons (Remove button inside) */}
				<div
					role="button"
					tabIndex={isUploading || isExtracting ? -1 : 0}
					aria-disabled={isUploading || isExtracting}
					className={`relative flex min-h-[200px] cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed backdrop-blur-md transition-all duration-300 ${
						isUploading || isExtracting
							? "cursor-not-allowed opacity-60"
							: dragActive
								? "border-primary bg-accent/60 shadow-[0_4px_24px_rgba(139,92,246,0.15)]"
								: value.file
									? "border-primary bg-accent/40 shadow-[0_2px_16px_rgba(139,92,246,0.1)]"
									: "border-border/50 bg-muted/20 hover:border-primary hover:bg-accent/30 hover:shadow-[0_2px_16px_rgba(0,0,0,0.08)]"
					}`}
					onDragEnter={handleDrag}
					onDragLeave={handleDrag}
					onDragOver={handleDrag}
					onDrop={handleDrop}
					onClick={() =>
						!isUploading && !isExtracting && inputRef.current?.click()
					}
					onKeyDown={handleFileUploadKeyDown}
				>
					<input
						id={fileInputId}
						type="file"
						accept=".pdf,.docx,.txt,.pptx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain,application/vnd.openxmlformats-officedocument.presentationml.presentation"
						className="hidden"
						ref={inputRef}
						onChange={handleFileChange}
					/>
					{value.file ? (
						<div className="flex flex-col items-center gap-2 p-4 text-center">
							<FileText className="h-12 w-12 text-primary" />
							<p className="text-sm font-medium text-foreground">
								{value.file.name}
							</p>
							<p className="text-xs text-muted-foreground">
								{(value.file.size / 1024 / 1024).toFixed(2)} MB
							</p>
							<Button
								variant="ghost"
								size="sm"
								onClick={handleRemoveFile}
								disabled={isUploading || isExtracting}
							>
								{t("workspace.create.remove")}
							</Button>
						</div>
					) : (
						<div className="flex flex-col items-center gap-2 p-4 text-center">
							<Upload className="h-12 w-12 text-muted-foreground" />
							<div>
								<p className="text-sm font-medium text-foreground">
									{t("workspace.create.dropFileHere")}
								</p>
								<p className="text-xs text-muted-foreground">
									{t("workspace.create.supportedFormats")}
								</p>
							</div>
						</div>
					)}
				</div>
			</div>

			{/* Show upload progress first, then extraction progress */}
			{isUploading && (
				<div className="grid gap-3 animate-in fade-in duration-300">
					<div className="flex items-center justify-between text-sm">
						<span className="text-muted-foreground flex items-center gap-2">
							<Loader2 className="h-4 w-4 animate-spin" />
							{t("workspace.create.uploadingFile")}
						</span>
						<span className="font-medium text-foreground tabular-nums">
							{uploadProgress}%
						</span>
					</div>
					<Progress
						value={uploadProgress}
						className="h-2.5 transition-all duration-300"
					/>
					<p className="text-xs text-muted-foreground text-center">
						{t("workspace.create.preparingFile")}
					</p>
				</div>
			)}

			{/* Only show extraction progress after upload is complete */}
			{!isUploading && isExtracting && (
				<div className="grid gap-3 animate-in fade-in duration-300">
					<div className="flex items-center justify-between text-sm">
						<span className="text-muted-foreground flex items-center gap-2">
							<Loader2 className="h-4 w-4 animate-spin" />
							{t("workspace.create.extractingFromFile")}
						</span>
						<span className="font-medium text-foreground tabular-nums">
							{extractionProgress}%
						</span>
					</div>
					<Progress
						value={extractionProgress}
						className="h-2.5 transition-all duration-300"
					/>
					<p className="text-xs text-muted-foreground text-center">
						{t("workspace.create.extractionTakesTime")}
					</p>
				</div>
			)}
		</div>
	);
}

function ConfigurationStep({
	paperTitle,
	onTitleChange,
	podcastLength,
	onPodcastLengthChange,
	language,
	onLanguageChange,
	chapterCount,
	costEstimates,
	userBalance,
	onShowPricingModal,
	extractionResult,
	trialRolling,
	estimateFailed,
	showRepeatIntent,
	onRepeatIntentCta,
}: {
	paperTitle: string;
	onTitleChange: (title: string) => void;
	podcastLength: PodcastLength;
	onPodcastLengthChange: (next: PodcastLength) => void;
	language: PodcastLanguage;
	onLanguageChange: (next: PodcastLanguage) => void;
	chapterCount: number | null;
	costEstimates: Record<PodcastLength, CostEstimate | null>;
	userBalance: number | null;
	onShowPricingModal: () => void;
	extractionResult: TextExtractionResult | null;
	trialRolling: TrialRollingSnapshot | null;
	estimateFailed: boolean;
	showRepeatIntent?: boolean;
	onRepeatIntentCta?: () => void;
}) {
	const { t } = useTranslation();
	const { subscriptionStatus } = useSubscription();
	// Paying subscribers who ran dry mid-cycle can top up instead of changing plan.
	const canTopUp = subscriptionStatus === "ACTIVE";
	const lengthRadioId = React.useId();
	const languageRadioId = React.useId();
	const titleInputId = React.useId();
	const [showAllLanguages, setShowAllLanguages] = React.useState(false);

	// Sort languages dynamically: Selected language always first
	const sortedLanguages = React.useMemo(() => {
		return [...PODCAST_LANGUAGES].sort((a, b) => {
			if (a.value === language) return -1;
			if (b.value === language) return 1;
			return 0;
		});
	}, [language]);

	const visibleLanguages = showAllLanguages
		? sortedLanguages
		: sortedLanguages.slice(0, 3);

	return (
		<div className="flex flex-col gap-3 sm:gap-5">
			{/* Title Input - Prominent and self-explanatory */}
			<div>
				<Label htmlFor={titleInputId} className="sr-only">
					{t("workspace.create.paperTitle")}
				</Label>
				<Input
					id={titleInputId}
					className="h-10 sm:h-12 text-base sm:text-lg font-medium border-2"
					placeholder={t("workspace.create.paperTitlePlaceholder")}
					value={paperTitle}
					onChange={(e) => onTitleChange(e.target.value)}
				/>
			</div>

			{/* Cost estimate could not be loaded */}
			{estimateFailed ? (
				<div className="rounded-xl bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800/50 p-4">
					<div className="flex items-start gap-3">
						<AlertCircle className="h-5 w-5 text-red-600 dark:text-red-500 flex-shrink-0 mt-0.5" />
						<div className="flex-1">
							<p className="text-sm font-medium text-red-900 dark:text-red-100 mb-1">
								{t("workspace.create.estimateFailedTitle")}
							</p>
							<p className="text-sm text-red-800 dark:text-red-200">
								{t("workspace.create.estimateFailedMessage")}
							</p>
						</div>
					</div>
				</div>
			) : null}

			{/* Repeat-intent trial nudge (N3) — second+ document for free users */}
			{showRepeatIntent && !estimateFailed ? (
				<div className="rounded-xl bg-gradient-to-br from-orange-500/5 via-amber-500/10 to-transparent border border-orange-500/25 p-4 shadow-sm relative overflow-hidden">
					<div className="absolute top-0 right-0 -mt-4 -mr-4 h-24 w-24 rounded-full bg-orange-500/10 blur-2xl" />
					<div className="flex flex-col gap-3 relative z-10">
						<div className="flex items-start gap-3">
							<div className="p-2 bg-orange-500/10 rounded-full shrink-0">
								<Crown className="h-4 w-4 text-orange-600 dark:text-orange-400" />
							</div>
							<div className="flex-1">
								<p className="text-sm font-semibold text-foreground mb-1">
									{t("nudges.repeatIntent.title")}
								</p>
								<p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
									{t("nudges.repeatIntent.description")}
								</p>
							</div>
						</div>
						<Button
							size="sm"
							onClick={onRepeatIntentCta}
							className="w-full bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white border-0"
						>
							{t("nudges.repeatIntent.cta")}
							<ArrowRight className="ml-2 h-3.5 w-3.5" />
						</Button>
					</div>
				</div>
			) : null}

			{/* Insufficient Text Warning */}
			{extractionResult?.hasInsufficientText ? (
				<div className="rounded-xl bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800/50 p-4 shadow-[0_2px_12px_rgba(239,68,68,0.1)] transition-all duration-300">
					<div className="flex items-start gap-3">
						<AlertCircle className="h-5 w-5 text-red-600 dark:text-red-500 flex-shrink-0 mt-0.5" />
						<div className="flex-1">
							<p className="text-sm font-medium text-red-900 dark:text-red-100 mb-1">
								{t("workspace.create.insufficientTextTitle")}
							</p>
							<p className="text-sm text-red-800 dark:text-red-200">
								{t("workspace.create.insufficientTextMessage", {
									totalLength: extractionResult.totalTextLength,
									chaptersWithText: extractionResult.chaptersWithText,
									totalChapters: extractionResult.totalChapters,
								})}
							</p>
						</div>
					</div>
				</div>
			) : null}

			{/* Low Chapter Count Warning */}
			{chapterCount !== null &&
			chapterCount <= 2 &&
			!extractionResult?.hasInsufficientText ? (
				<div className="rounded-xl bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800/50 p-4 shadow-[0_2px_12px_rgba(245,158,11,0.1)] transition-all duration-300">
					<div className="flex items-start gap-3">
						<AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-500 flex-shrink-0 mt-0.5" />
						<div className="flex-1">
							<p className="text-sm font-medium text-amber-900 dark:text-amber-100 mb-1">
								{t("workspace.create.limitedChaptersTitle")}
							</p>
							<p className="text-sm text-amber-800 dark:text-amber-200">
								{t("workspace.create.limitedChaptersMessage", {
									count: chapterCount,
									plural: chapterCount === 1 ? "" : "s",
								})}
							</p>
						</div>
					</div>
				</div>
			) : null}

			{/* Language Selection - Self-explanatory with flags */}
			<div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
				<div
					className="flex gap-1.5 sm:gap-2 flex-wrap"
					role="radiogroup"
					aria-label={t("workspace.create.podcastLanguage")}
				>
					{visibleLanguages.map((lang) => {
						const isSelected = language === lang.value;
						const id = `${languageRadioId}-${lang.value}`;

						return (
							<label key={lang.value} htmlFor={id} className="block relative">
								<input
									type="radio"
									id={id}
									name="podcast-language"
									className="peer sr-only"
									checked={isSelected}
									onChange={() => onLanguageChange(lang.value)}
									aria-label={lang.label}
								/>
								<Card
									className={`relative overflow-hidden rounded-md py-1.5 px-3 sm:py-2 sm:px-3.5 transition-all duration-200 cursor-pointer peer-focus-visible:outline-hidden peer-focus-visible:ring-2 peer-focus-visible:ring-primary ${
										isSelected
											? "border-primary bg-primary/5 ring-1 ring-primary"
											: "border-border/40 bg-card/20 hover:border-border"
									}`}
									onClick={() => onLanguageChange(lang.value)}
								>
									<span
										className={`font-medium text-xs tracking-wide transition-colors ${
											isSelected ? "text-primary" : "text-foreground"
										}`}
									>
										{lang.flag} {lang.label}
									</span>
								</Card>
							</label>
						);
					})}
					<button
						type="button"
						onClick={() => setShowAllLanguages(!showAllLanguages)}
						className="relative overflow-hidden rounded-md py-1.5 px-3 sm:py-2 sm:px-3.5 transition-all duration-200 cursor-pointer border border-border/40 bg-card/20 hover:border-border hover:bg-card/40 flex items-center justify-center gap-1"
						aria-label={
							showAllLanguages
								? t("workspace.create.lessLanguages")
								: t("workspace.create.moreLanguages")
						}
					>
						<span className="font-medium text-xs text-muted-foreground tracking-wide">
							{showAllLanguages
								? t("workspace.create.lessLanguages")
								: t("workspace.create.moreLanguages")}
						</span>
						{showAllLanguages ? (
							<ChevronUp className="h-3.5 w-3.5 text-muted-foreground" />
						) : (
							<MoreHorizontal className="h-3.5 w-3.5 text-muted-foreground" />
						)}
					</button>
				</div>
				<div className="hidden sm:block h-4 w-px bg-border/60 mx-1" />
				<div
					className="flex items-center gap-1.5 text-xs text-muted-foreground/60 mt-1 sm:mt-0"
					title={t("workspace.create.podcastLanguage")}
				>
					<Languages className="h-3.5 w-3.5" />
					<span>{t("workspace.create.outputLanguage") || "Output"}</span>
				</div>
			</div>

			{/* Subtle divider */}
			<div className="h-px bg-border/50 -mx-3 sm:mx-0" />

			{/* Podcast Length - Cards are self-explanatory with icons + durations */}
			<div
				className="space-y-2"
				role="radiogroup"
				aria-label={t("workspace.create.podcastLength")}
			>
				<div className="space-y-2">
					{podcastLengthsBase.map((length) => {
						const Icon = length.icon;
						const isSelected = podcastLength === length.value;
						const estimate = costEstimates[length.value];
						const blockedByTrialCap =
							estimate !== null &&
							trialRolling !== null &&
							trialRolling.used + estimate.totalCost > trialRolling.limit;
						const canAfford =
							userBalance !== null &&
							estimate !== null &&
							userBalance >= estimate.totalCost &&
							!blockedByTrialCap;
						const id = `${lengthRadioId}-${length.value}`;

						return (
							<label key={length.value} htmlFor={id} className="block relative">
								<input
									type="radio"
									id={id}
									name="podcast-length"
									className="peer sr-only"
									checked={isSelected}
									onChange={() => onPodcastLengthChange(length.value)}
									aria-label={`${t(length.labelKey)}: ${estimate ? `${estimate.estimatedAudioMinutes} ${t("workspace.create.minutes")}` : t("common.loading")}`}
								/>
								<Card
									className={`transition-all py-1.5 sm:py-2 duration-200 peer-focus-visible:outline-hidden ${
										isSelected
											? "cursor-pointer border-primary bg-primary/5 ring-1 ring-primary"
											: "cursor-pointer border-border/40 bg-card/20 hover:border-border hover:bg-card/40"
									}`}
									onClick={() => onPodcastLengthChange(length.value)}
								>
									<div className="flex items-center p-2 sm:p-4 gap-2 sm:gap-4">
										{/* Icon */}
										<div
											className={`p-1.5 sm:p-2 rounded-lg flex items-center justify-center h-8 w-8 sm:h-11 sm:w-11 shrink-0 ${
												isSelected ? "bg-primary/10" : "bg-secondary/40"
											}`}
										>
											<Icon
												className={`h-4 w-4 sm:h-5 sm:w-5 ${length.colorClass}`}
											/>
										</div>

										{/* Main Content */}
										<div className="flex-1 min-w-0">
											<div className="flex items-center gap-1.5 sm:gap-2 mb-0.5 sm:mb-1">
												<span className="font-semibold text-sm sm:text-base text-foreground">
													{t(length.labelKey)}
												</span>
												{length.recommended && (
													<Badge
														variant="secondary"
														className="bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20 px-1.5 sm:px-2 py-0.5 h-4 sm:h-5 text-[9px] sm:text-[10px] font-medium"
													>
														{t("common.recommended") || "Empfohlen"}
													</Badge>
												)}
											</div>
											<div className="flex items-center gap-1.5 sm:gap-3 text-xs sm:text-sm text-muted-foreground">
												{estimate && (
													<>
														<span className="font-semibold sm:font-bold">
															≈ {estimate.estimatedAudioMinutes} min
														</span>
														{chapterCount && (
															<>
																<span className="text-[10px] sm:text-sm">
																	•
																</span>
																<span>
																	{chapterCount}{" "}
																	{t("workspace.create.chapters")}
																</span>
															</>
														)}
													</>
												)}
											</div>
										</div>

										{/* Cost - Small and subtle */}
										<div className="text-right shrink-0">
											{estimate ? (
												<>
													{!canAfford ? (
														<div className="flex flex-col items-end gap-1">
															<Badge
																variant="outline"
																className="bg-amber-50 dark:bg-amber-950/20 text-amber-600 dark:text-amber-400 border-amber-200 dark:border-amber-800/50 px-1.5 sm:px-2 py-0.5 sm:py-1 h-5 sm:h-6 text-[10px] sm:text-xs font-medium flex items-center gap-1"
															>
																<Crown className="h-2.5 w-2.5 sm:h-3 sm:w-3" />
																<span className="text-[10px] sm:text-xs">
																	{blockedByTrialCap &&
																	userBalance !== null &&
																	userBalance >= estimate.totalCost
																		? t("workspace.create.trialRollingBadge")
																		: estimate.totalCost}
																</span>
															</Badge>
														</div>
													) : (
														<span className="text-[10px] sm:text-xs text-muted-foreground/60">
															{estimate.totalCost} {t("coins.coins")}
														</span>
													)}
												</>
											) : (
												<Loader2 className="h-3.5 w-3.5 sm:h-4 sm:w-4 animate-spin text-muted-foreground" />
											)}
										</div>
									</div>
								</Card>
							</label>
						);
					})}
				</div>

				{/* Balance info and Warnings */}
				{userBalance !== null &&
					(() => {
						const selectedEstimate = costEstimates[podcastLength];
						const trialBlocksSelection =
							selectedEstimate !== null &&
							trialRolling !== null &&
							trialRolling.used + selectedEstimate.totalCost >
								trialRolling.limit;
						const selectedIsUnaffordable =
							selectedEstimate && userBalance < selectedEstimate.totalCost;

						if (
							trialBlocksSelection &&
							selectedEstimate &&
							userBalance >= selectedEstimate.totalCost
						) {
							return (
								<div className="rounded-xl bg-gradient-to-br from-emerald-500/5 via-emerald-500/10 to-transparent border border-emerald-500/25 p-4 shadow-sm mt-3 relative overflow-hidden">
									<div className="absolute top-0 right-0 -mt-4 -mr-4 h-24 w-24 rounded-full bg-emerald-500/10 blur-2xl" />
									<div className="flex flex-col gap-3 relative z-10">
										<div className="flex items-start gap-3">
											<div className="p-2 bg-emerald-500/10 rounded-full shrink-0">
												<Crown className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
											</div>
											<div className="flex-1">
												<p className="text-sm font-semibold text-foreground mb-1">
													{t("workspace.create.trialRollingCapTitle")}
												</p>
												<p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
													{t("workspace.create.trialRollingCapDescription", {
														used: trialRolling.used,
														limit: trialRolling.limit,
														cost: selectedEstimate.totalCost,
													})}
												</p>
											</div>
										</div>
										<Button
											size="sm"
											onClick={onShowPricingModal}
											className="w-full bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_2px_8px_rgba(16,185,129,0.2)]"
										>
											{t("workspace.create.trialRollingCapAction")}
											<ArrowRight className="ml-2 h-3.5 w-3.5" />
										</Button>
									</div>
								</div>
							);
						}

						if (selectedIsUnaffordable && selectedEstimate) {
							const missingCoins = selectedEstimate.totalCost - userBalance;
							return (
								<div className="rounded-xl bg-gradient-to-br from-primary/5 via-primary/10 to-transparent border border-primary/20 p-4 shadow-sm mt-3 relative overflow-hidden">
									{/* Background decoration */}
									<div className="absolute top-0 right-0 -mt-4 -mr-4 h-24 w-24 rounded-full bg-primary/10 blur-2xl" />

									<div className="flex flex-col gap-3 relative z-10">
										<div className="flex items-start gap-3">
											<div className="p-2 bg-primary/10 rounded-full shrink-0">
												<Crown className="h-4 w-4 text-primary" />
											</div>
											<div className="flex-1">
												<p className="text-sm font-semibold text-foreground mb-1">
													{t("workspace.create.unlockPodcastTitle")}
												</p>
												<p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
													{canTopUp
														? t(
																"workspace.create.unlockPodcastDescriptionTopUp",
																{ missing: missingCoins },
															)
														: t("nudges.outOfCoins.quantified", {
																required: selectedEstimate.totalCost,
																balance: userBalance,
																missing: missingCoins,
															})}
												</p>
											</div>
										</div>

										<Button
											size="sm"
											onClick={onShowPricingModal}
											className="w-full bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_2px_8px_rgba(139,92,246,0.25)]"
										>
											{t(
												canTopUp
													? "workspace.create.topUpCoinsAction"
													: "workspace.create.getCoinsAction",
											)}
											<ArrowRight className="ml-2 h-3.5 w-3.5" />
										</Button>
									</div>
								</div>
							);
						}

						const anyUnaffordable = Object.values(costEstimates).some(
							(estimate) => estimate && userBalance < estimate.totalCost,
						);
						if (anyUnaffordable) {
							return (
								<p className="text-xs text-muted-foreground/60 text-center pt-1">
									{t("workspace.create.coinsBalance", { balance: userBalance })}
								</p>
							);
						}
						return null;
					})()}
			</div>
		</div>
	);
}

export type {
	KnowledgeLevel,
	PodcastLength,
	PodcastLanguage,
	UploadedPaper,
	CreatePodWorkspaceModalProps,
};
